/**
 * Shared postMessage dispatcher for the workeredux bridge, used by both sides
 * (client/createMiddleware.ts and worker/createStore.ts).
 *
 * Returns [dispatch, close]. In default mode actions are posted immediately; in batchMode
 * they are queued and flushed by a 33ms interval, which posts contiguous plain actions as
 * one array per tick. Actions carrying transferables (see ./transferables.ts) are always
 * posted alone with their transfer list so ArrayBuffers move instead of copy. The interval
 * runs regardless of mode, so callers must invoke the returned close() on teardown.
 */
import { Action } from '@reduxjs/toolkit';
import {
  TRANSFERABLE_FLAG,
  hasTransferables,
  ActionWithTransferable,
} from './transferables';
import { arrayHasContents } from '@common/shared/util';

const INTERVAL_MS = 33;

export const actionBuffer = (
  worker: Worker | DedicatedWorkerGlobalScope,
  batchMode = false
): [(a: Action) => void, () => void] => {
  const buffer: (Action | ActionWithTransferable<any>)[] = [];

  const ref = setInterval(() => {
    if (!arrayHasContents(buffer)) {
      return;
    }
    try {
      if (hasTransferables(buffer[0])) {
        const action = buffer.shift();
        if (action && hasTransferables(action)) {
          worker.postMessage(action, action[TRANSFERABLE_FLAG]);
        }
        return;
      }
      const actions: Action[] = [];
      while (buffer.length > 0 && !hasTransferables(buffer[0])) {
        const action = buffer.shift();
        if (action) {
          actions.push(action);
        }
      }
      if (actions.length > 0) {
        worker.postMessage(actions.length > 1 ? actions : actions[0]);
      }
    } catch (err) {
      console.error('Failed to postMessage.', err);
    }
  }, INTERVAL_MS);

  return [
    (action: Action) => {
      if (batchMode) {
        buffer.push(action);
      } else {
        if (hasTransferables(action)) {
          worker.postMessage(action, action[TRANSFERABLE_FLAG]);
        } else {
          worker.postMessage(action);
        }
      }
    },
    () => clearInterval(ref),
  ];
};
